// packages/web-ui/src/AppShell/DragHandle.tsx
//
// W2-S2 AC #2 — hand-rolled resize handle. 4 px wide hot-zone, ew-resize cursor,
// hover-highlight via Tailwind, double-click reset, pointer-events drag.
// (Pitfall #6: avoid `@base-ui/react`'s `Resizable` until it's GA'd.)

import { useCallback, useEffect, useRef } from 'react';
import type { JSX, PointerEvent as ReactPointerEvent } from 'react';
import { RESIZE_HANDLE_WIDTH_PX, Z_INDEX } from './responsive-tokens.js';

export interface DragHandleProps {
  /** "left" → drag right makes the LEFT-side panel wider; "right" → drag left makes the RIGHT-side panel wider. */
  side: 'left' | 'right';
  /** Current width of the panel (used to compute the new width on drag). */
  currentWidth: number;
  /** Drag-handle is disabled when the host panel is in overlay/popover/hidden mode. */
  disabled?: boolean;
  onResize: (newWidth: number) => void;
  onReset: () => void;
  testId?: string;
}

export function DragHandle({
  side,
  currentWidth,
  disabled = false,
  onResize,
  onReset,
  testId,
}: DragHandleProps): JSX.Element {
  const dragStartXRef = useRef<number | null>(null);
  const dragStartWidthRef = useRef<number>(currentWidth);
  const onResizeRef = useRef(onResize);

  useEffect(() => {
    onResizeRef.current = onResize;
  }, [onResize]);

  const handleMove = useCallback(
    (event: PointerEvent) => {
      if (dragStartXRef.current === null) return;
      const dx = event.clientX - dragStartXRef.current;
      // Left-side handle expands the LEFT panel as the pointer moves right.
      // Right-side handle expands the RIGHT panel as the pointer moves left.
      const next =
        side === 'left' ? dragStartWidthRef.current + dx : dragStartWidthRef.current - dx;
      onResizeRef.current(next);
    },
    [side],
  );

  const handleUp = useCallback(() => {
    dragStartXRef.current = null;
    if (typeof window !== 'undefined') {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    }
  }, [handleMove]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      if (disabled) return;
      dragStartXRef.current = event.clientX;
      dragStartWidthRef.current = currentWidth;
      if (typeof window !== 'undefined') {
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
      }
    },
    [currentWidth, disabled, handleMove, handleUp],
  );

  const onDoubleClick = useCallback((): void => {
    if (disabled) return;
    onReset();
  }, [disabled, onReset]);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-disabled={disabled || undefined}
      data-testid={testId ?? `drag-handle-${side}`}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      className={
        disabled
          ? 'pointer-events-none'
          : 'hover:bg-taupe-300 cursor-ew-resize transition-colors'
      }
      style={{
        width: RESIZE_HANDLE_WIDTH_PX,
        zIndex: Z_INDEX.resizeHandle,
        flexShrink: 0,
      }}
    />
  );
}
